/**
 * Calendar-day arithmetic in UTC.
 *
 * Every date in this codebase that means "a day" rather than "an instant" —
 * an order's `orderedDate`, a filter bound, a day cell in a calendar grid —
 * has to be handled in UTC, because the local-timezone accessors shift the day
 * for anyone west of Greenwich. A date-only ISO string (`2026-08-04`) parses as
 * UTC midnight, so `new Date("2026-08-04").getDate()` is **3** in New York.
 *
 * That is not hypothetical: it is the bug SFRA's expense filters ship today
 * (`expenseHelper.js` builds `MM/DD/YYYY` from instance-local getters and
 * compares it against a Site-timezone rebuild), and the reason the plan lists
 * the date filter under "do NOT replicate". These helpers exist so the filter
 * panel and the loader cannot disagree about what day it is.
 *
 * Deliberately dependency-free: the repo has no date library, and this is a
 * few dozen lines of `Date.UTC` arithmetic rather than a reason to add one.
 */

/** A calendar day as `YYYY-MM-DD`. Not an instant — no time, no zone. */
export type IsoDay = string;

const ISO_DAY_SHAPE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Round-trips through `Date` to reject impossible days that the shape test
 * lets through: `2026-13-45` has no such month, and `2026-02-30` is silently
 * rolled into March 2 rather than rejected. Anything that does not come back
 * as the exact string it went in as is not a real day.
 */
export function isIsoDay(value: unknown): value is IsoDay {
  return (
    typeof value === "string" &&
    ISO_DAY_SHAPE.test(value) &&
    toIsoDay(new Date(value)) === value
  );
}

/** `Date` → `YYYY-MM-DD`. `toISOString` is always UTC, so slicing is safe. */
export function toIsoDay(date: Date): IsoDay | null {
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 10);
}

/**
 * Any ISO string (day or full timestamp) → its UTC day. `null` when
 * unparseable, so callers branch rather than comparing against `"Invalid Date"`.
 */
export function utcDayOf(iso: string): IsoDay | null {
  return toIsoDay(new Date(iso));
}

/** Year / month (0-11) / day → `YYYY-MM-DD`. */
export function isoDayFrom(year: number, month: number, day: number): IsoDay {
  return new Date(Date.UTC(year, month, day)).toISOString().slice(0, 10);
}

/** `YYYY-MM-DD` → `{ year, month (0-11), day }`. Assumes a valid IsoDay. */
export function partsOf(iso: IsoDay): {
  year: number;
  month: number;
  day: number;
} {
  const [year, month, day] = iso.split("-").map(Number);
  return { year, month: month - 1, day };
}

/**
 * Inclusive on both ends — a range of one day matches that day, which is what
 * makes a range picker a strict superset of the single-date filter it replaces.
 * An open end means unbounded in that direction, so a shopper can ask for
 * "everything since March" without inventing an end date.
 */
export function isWithinRange(
  day: IsoDay,
  from: IsoDay | undefined,
  to: IsoDay | undefined,
): boolean {
  if (from && day < from) return false;
  if (to && day > to) return false;
  return true;
}

/** Days in a UTC month. Day 0 of the next month is the last of this one. */
export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
}

/** Weekday of the 1st, 0 = Sunday — the offset the grid's first row needs. */
export function firstWeekdayOfMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 1)).getUTCDay();
}

/** Month shifted by `delta`, normalised. `Date.UTC` handles year rollover. */
export function shiftMonth(
  year: number,
  month: number,
  delta: number,
): { year: number; month: number } {
  const shifted = new Date(Date.UTC(year, month + delta, 1));
  return { year: shifted.getUTCFullYear(), month: shifted.getUTCMonth() };
}

/**
 * `YYYY-MM-DD` → `MM/DD/YYYY` for display only.
 *
 * Built by string surgery rather than `toLocaleDateString`, which formats in
 * the VIEWER's timezone and would print the previous day for most of the US —
 * the exact defect this module exists to prevent. Never parse this back; the
 * ISO value is the source of truth and what travels in the URL.
 */
export function formatIsoDayUS(iso: IsoDay): string {
  const [year, month, day] = iso.split("-");
  return `${month}/${day}/${year}`;
}
